'use client';

import * as React from 'react';
import { Control } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useFieldInputMergeProps } from '@/hooks/use-merge-props';
import {
  InputPrefixSuffixCds,
  InputPrefixSuffixCdsProps,
} from '@/components/cds/input-prefix-suffix-cds';

/**
 * Props for the FormFieldInputPrefixSuffixCds component.
 *
 * @typedef {Object} FormFieldInputPrefixSuffixCdsProps
 * @property {Control<any, any>} [control] - The control object for managing form state.
 * @property {string | React.ReactNode} [label] - The label for the input field.
 * @property {string | React.ReactNode} [description] - The description for the input field.
 * @property {string | React.ReactNode} [errorMessage] - The error message to display when validation fails.
 *
 * @extends {Omit<React.ComponentProps<'input'>, 'form'>}
 * @extends {InputPrefixSuffixCdsProps}
 */
export type FormFieldInputPrefixSuffixCdsProps = Omit<React.ComponentProps<'input'>, 'form'> &
  InputPrefixSuffixCdsProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control?: Control<any, any>;
    label?: string | React.ReactNode;
    description?: string | React.ReactNode;
    errorMessage?: string | React.ReactNode;
    inputClassName?: string;
  };

/**
 * `FormFieldInputPrefixSuffixCds` is a React component that renders a form field with optional prefix and suffix input elements.
 * It uses `React.forwardRef` to pass a ref to the underlying input element.
 *
 * @param {string} className - Optional additional class name(s) for the form item.
 * @param {string} placeholder - Optional placeholder text for the input field.
 * @param {string} label - Optional label text for the form field.
 * @param {any} control - Control object for managing form state.
 * @param {string} name - Name attribute for the input field. This is required.
 * @param {string} description - Optional description text for the form field.
 * @param {string} errorMessage - Optional error message to display.
 * @param {string} inputClassName - Optional additional class name(s) for the input element.
 * @param {React.Ref<HTMLInputElement>} ref - Ref to be forwarded to the input element.
 * @param {object} props - Additional props to be passed to the form field.
 *
 * @throws {Error} Throws an error if the `name` prop is not provided.
 *
 * @returns {JSX.Element} The rendered form field component.
 */
const FormFieldInputPrefixSuffixCds = React.forwardRef<
  HTMLInputElement,
  FormFieldInputPrefixSuffixCdsProps
>(
  (
    {
      className,
      placeholder,
      label,
      control,
      name,
      description,
      errorMessage,
      inputClassName,
      ...props
    },
    ref
  ) => {
    const mergeProps = useFieldInputMergeProps(props);

    if (!name) {
      throw new Error(`FormFieldInputPrefixSuffixCds 'name' is required (label: ${label})`);
    }

    return (
      <FormField
        {...props}
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className={className} ref={ref}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <InputPrefixSuffixCds
                {...mergeProps(field)}
                className={inputClassName}
                placeholder={placeholder || ''}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage>{errorMessage || ''}</FormMessage>
          </FormItem>
        )}
      />
    );
  }
);
FormFieldInputPrefixSuffixCds.displayName = 'FormFieldInputPrefixSuffixCds';

export { FormFieldInputPrefixSuffixCds };
